import type { Order } from '@shared/types';

interface DayRow {
  date: string;
  revenue: number;
  count: number;
}

interface Props {
  orders: Order[];
}

export default function TopRevenueDays({ orders }: Props) {
  const completed = orders.filter((o) => o.status === 'completed');
  if (completed.length === 0) return null;

  const buckets: Record<string, { revenue: number; count: number }> = {};
  for (const o of completed) {
    const date = new Date(o.created_at).toISOString().slice(0, 10);
    if (!buckets[date]) buckets[date] = { revenue: 0, count: 0 };
    buckets[date].revenue += o.verified_amount || o.expected_amount;
    buckets[date].count++;
  }

  const days: DayRow[] = Object.entries(buckets)
    .map(([date, d]) => ({ date, ...d }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const maxRevenue = days[0]?.revenue || 1;

  return (
    <div
      className="rounded p-5"
      style={{
        border: '1px solid var(--dash-border)',
        background: 'var(--dash-card)',
      }}
    >
      <div
        className="font-mono text-[10px] uppercase tracking-widest mb-4"
        style={{ color: 'var(--dash-text-dim)' }}
      >
        Top Revenue Days
      </div>
      <div className="space-y-3">
        {days.map((day, i) => (
          <div key={day.date} className="flex items-center gap-3">
            <span
              className="font-mono text-[10px] w-4 shrink-0 text-right"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              {i + 1}.
            </span>
            <span
              className="font-mono text-xs w-20 shrink-0"
              style={{ color: 'var(--dash-text-tertiary)' }}
            >
              {day.date}
            </span>
            <div
              className="flex-1 h-5 rounded overflow-hidden relative"
              style={{ background: 'var(--dash-hover-bg)' }}
            >
              <div
                className="h-full bg-emerald-500/20 rounded transition-all duration-300"
                style={{ width: `${(day.revenue / maxRevenue) * 100}%` }}
              />
              <div className="absolute inset-0 flex items-center px-2">
                <span
                  className="font-mono text-[10px]"
                  style={{ color: 'var(--dash-text-muted)' }}
                >
                  ${day.revenue.toFixed(2)}
                </span>
              </div>
            </div>
            <span
              className="font-mono text-[10px] w-16 shrink-0 text-right"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              {day.count} order{day.count !== 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
